import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Upload, X, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

import { isTauri } from "@/lib/db/db";

interface PhotoCaptureProps {
  onPhotoSelected: (photoUrl: string) => void;
  currentPhoto?: string;
}

const PhotoCapture = ({ onPhotoSelected, currentPhoto }: PhotoCaptureProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentPhoto || null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const { toast } = useToast();

  const checkCameraSupport = () => {
    console.log('Verificando suporte à câmera...');
    
    // Verificar se está em contexto seguro (HTTPS)
    if (!window.isSecureContext) {
      return {
        supported: false,
        error: 'A câmera só funciona em conexões seguras (HTTPS). Tente carregar um arquivo em vez disso.'
      };
    }
    
    // Verificar se navigator.mediaDevices está disponível
    if (!navigator.mediaDevices) {
      return {
        supported: false,
        error: 'Seu navegador não suporta acesso à câmera. Tente carregar um arquivo em vez disso.'
      };
    }
    
    // Verificar se getUserMedia está disponível
    if (!navigator.mediaDevices.getUserMedia) {
      return {
        supported: false,
        error: 'Função getUserMedia não disponível. Tente carregar um arquivo em vez disso.'
      };
    }
    
    console.log('Suporte à câmera verificado: OK');
    return { supported: true };
  };

  const startCamera = async () => {
    console.log('Tentando iniciar câmera...');
    setIsStartingCamera(true);
    setCameraError(null);
    
    try {
      // Verificar suporte primeiro
      const supportCheck = checkCameraSupport();
      if (!supportCheck.supported) {
        throw new Error(supportCheck.error);
      }

      console.log('Solicitando permissão da câmera...');
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 640 }, 
            height: { ideal: 480 },
            facingMode: 'user'
          } 
        });
      } catch (err) {
        console.warn('Falha ao obter stream com constraints ideais/facingMode. Tentando simplificado...', err);
        try {
          stream = await navigator.mediaDevices.getUserMedia({ 
            video: true 
          });
        } catch (innerErr) {
          throw innerErr;
        }
      }
      
      console.log('Câmera acessada com sucesso!');
      
      // Armazena o stream e ativa a câmera primeiro
      streamRef.current = stream;
      setIsCameraActive(true);
      setCameraError(null);
    } catch (error: any) {
      console.error('Erro ao acessar câmera:', error);
      
      let errorMessage = 'Erro desconhecido ao acessar a câmera';
      const errorMsgStr = String(error.message || '').toLowerCase();
      
      if (error.name === 'NotAllowedError' || errorMsgStr.includes('permission denied')) {
        errorMessage = 'Permissão de acesso à câmera negada. Certifique-se de conceder acesso nas configurações de privacidade do Windows ou do aplicativo.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera encontrada no dispositivo.';
      } else if (error.name === 'NotReadableError' || errorMsgStr.includes('could not start video source') || errorMsgStr.includes('readable')) {
        errorMessage = 'Não foi possível iniciar a câmera. Ela pode estar em uso por outro aplicativo (ex: OBS, Zoom, Teams, WhatsApp, Navegador) ou desabilitada.';
      } else if (error.name === 'SecurityError') {
        errorMessage = 'Acesso à câmera bloqueado por política de segurança.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setCameraError(errorMessage);
      
      toast({
        title: "Erro ao acessar câmera",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsStartingCamera(false);
    }
  };

  const stopCamera = () => {
    console.log('Parando câmera...');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setCameraError(null);
  };

  // useEffect para conectar o stream ao video quando ambos estiverem prontos
  const [, forceUpdate] = useState({});
  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      const video = videoRef.current;
      video.srcObject = streamRef.current;
      video.onloadedmetadata = () => {
        video.play().catch(err => console.log('Erro ao tocar video:', err));
      };
    }
  }, [isCameraActive, forceUpdate]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            stopCamera();
          }
        }, 'image/jpeg', 0.8);
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "Arquivo muito grande",
          description: "A imagem deve ter no máximo 5MB.",
          variant: "destructive",
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: "Formato inválido",
          description: "Apenas arquivos de imagem são permitidos.",
          variant: "destructive",
        });
        return;
      }

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const uploadPhoto = async () => {
    if (!previewUrl) return;

    setIsUploading(true);
    try {
      let file: File;
      
      if (previewUrl.startsWith('blob:')) {
        const response = await fetch(previewUrl);
        const blob = await response.blob();
        file = new File([blob], `visitor_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      } else {
        onPhotoSelected(previewUrl);
        setIsOpen(false);
        setIsUploading(false);
        return;
      }

      if (isTauri()) {
        const { writeFile, BaseDirectory, exists, mkdir } = await import("@tauri-apps/plugin-fs");
        const { appLocalDataDir, join } = await import("@tauri-apps/api/path");

        const appDataDirPath = await appLocalDataDir();
        
        // Garante a existência do diretório
        const dirExists = await exists("images", { baseDir: BaseDirectory.AppLocalData });
        if (!dirExists) {
          await mkdir("images", { baseDir: BaseDirectory.AppLocalData });
        }

        const uniqueName = `captured_${Date.now()}.jpg`;
        const relativePath = `images/${uniqueName}`;
        const absolutePath = await join(appDataDirPath, relativePath);

        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        await writeFile(relativePath, uint8Array, { baseDir: BaseDirectory.AppLocalData });

        onPhotoSelected(absolutePath);
        setIsOpen(false);
        toast({
          title: "Foto salva localmente!",
          description: "A foto do visitante foi gravada no computador.",
        });
        return;
      }

      // Fallback em desenvolvimento web
      onPhotoSelected(previewUrl);
      setIsOpen(false);
      toast({
        title: "Foto vinculada (Desenvolvimento)",
        description: "Foto temporária vinculada ao formulário.",
      });
    } catch (error) {
      console.error('Error uploading/saving photo:', error);
      toast({
        title: "Erro ao salvar foto",
        description: "Não foi possível gravar a imagem localmente.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };


  const removePhoto = () => {
    setPreviewUrl(null);
    onPhotoSelected('');
  };

  return (
    <>
      <div className="space-y-2">
        <Label>Foto do Colaborador</Label>
        <div className="flex items-center gap-2">
          {previewUrl ? (
            <div className="relative">
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="w-20 h-20 object-cover rounded-lg border"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={removePhoto}
                className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="w-20 h-20 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
              <Camera className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2"
          >
            <Camera className="h-4 w-4" />
            {previewUrl ? 'Alterar Foto' : 'Adicionar Foto'}
          </Button>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          stopCamera();
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Capturar ou Selecionar Foto</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {isCameraActive ? (
              <div className="space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3]">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    onCanPlay={() => forceUpdate({})}
                  />
                </div>
                <canvas ref={canvasRef} className="hidden" />
                <div className="flex gap-2">
                  <Button onClick={capturePhoto} className="flex-1 bg-primary hover:bg-primary/90">
                    <Camera className="h-4 w-4 mr-2" />
                    Capturar Foto
                  </Button>
                  <Button variant="outline" onClick={stopCamera}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {previewUrl && (
                  <div className="text-center">
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      className="max-w-full max-h-64 mx-auto rounded-lg"
                    />
                  </div>
                )}
                
                {cameraError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-destructive">
                      <p className="font-medium">Problema com a câmera:</p>
                      <p>{cameraError}</p>
                      {cameraError.includes('permissão') && (
                        <p className="mt-1 text-xs">
                          Dica: Recarregue a página e clique em "Permitir" quando solicitado.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startCamera}
                    disabled={isStartingCamera}
                    className="flex items-center gap-2"
                  >
                    {isStartingCamera ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {isStartingCamera ? 'Acessando...' : 'Usar Câmera'}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Carregar Arquivo
                  </Button>
                </div>
                
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {previewUrl && (
                  <div className="flex gap-2">
                    <Button
                      onClick={uploadPhoto}
                      disabled={isUploading}
                      className="flex-1"
                    >
                      {isUploading ? "Enviando..." : "Confirmar Foto"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setPreviewUrl(null)}
                    >
                      Remover
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhotoCapture;
